import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Box,
  Typography,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { auth } from "../config/firebaseConfig";
import { useNavigate } from "react-router-dom";
import useStore from "../config/store";
import { useTranslation } from 'react-i18next';
import PointsUsage from "../PointsUsage";
import api from "../config/api";

const TopBar = ({ drawerWidth = 240 }) => {
  const navigate = useNavigate();
  const {
    user,
    resetStore,
    organization,
    project,
    plan,
    setOrganization,
    setProject,
    appMode,
    setAppMode,
    erpEnabled,
    setErpEnabled,
    erpRole,
    setErpRole,
    atsEnabled,
    huntingEnabled,
    setOrganizationModules,
    authToken,
  } = useStore();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Modo ERP: habilitado por organización (campo erpEnabled del doc de
  // organización, seteado por super-admin vía PUT /erp/settings). Se consulta
  // al cambiar de organización (y al refrescar token) y se persiste en el
  // store para que el toggle y las rutas /erp reaccionen.
  useEffect(() => {
    const fetchErpEnabled = async () => {
      if (!organization) {
        setErpEnabled(false);
        return;
      }
      if (!authToken) return;
      try {
        const response = await api.get('/erp/enabled', {
          params: { organizationId: organization },
        });
        const enabled = response.data?.response?.data?.enabled === true;
        const role = response.data?.response?.data?.erpRole || null;
        setErpEnabled(enabled);
        setErpRole(role);
        // Usuario "solo ERP": aterriza directo en el módulo
        if (enabled && role && user?.rol === 'user' && appMode !== 'erp') {
          setAppMode('erp');
        }
        // Si el modo activo quedó sin habilitación (p. ej. cambio de
        // organización), volver al modo evaluaciones.
        if ((!enabled || (user?.rol !== 'super-admin' && !role)) && appMode === 'erp') {
          setAppMode('legacy');
          navigate('/home');
        }
      } catch (err) {
        // Error transitorio: conservar el último valor conocido
        console.error('Error consultando habilitación ERP:', err);
      }
    };
    fetchErpEnabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, authToken]);

  // Modos ATS y Hunting: módulos de la organización (campos atsEnabled y
  // huntingEnabled; los enciende un super-admin en "Editar organización",
  // 2026-10-26 — antes eran listas fijas en este archivo). Se consultan al
  // cambiar de organización (y al refrescar token). Si el modo activo quedó
  // apagado, se vuelve a Evaluaciones.
  useEffect(() => {
    const fetchModules = async () => {
      if (!organization) {
        setOrganizationModules({ ats: null, hunting: null });
        return;
      }
      if (!authToken) return;
      try {
        const response = await api.get('/organizations/modules', {
          params: { organizationId: organization },
        });
        const data = response.data?.response?.data || {};
        // La respuesta es de esta organización (pudo cambiar mientras tanto)
        if (useStore.getState().organization !== organization) return;
        const ats = data.ats === true;
        const hunting = data.hunting === true;
        setOrganizationModules({ ats, hunting });
        const currentMode = useStore.getState().appMode;
        if ((currentMode === 'ats' && !ats) || (currentMode === 'hunting' && !hunting)) {
          setAppMode('legacy');
          navigate('/home');
        }
      } catch (err) {
        // Error transitorio: conservar el último valor conocido
        console.error('Error consultando los módulos de la organización:', err);
      }
    };
    fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, authToken]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      resetStore();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const handleChangeOrganization = () => {
    setProject("");
    setOrganization("");
    navigate("/organizations");
  };

  const handleChangeProject = () => {
    setProject("");
    navigate(`/organizations/${organization}`);
  };

  const logoColor = "#17293C";
  const userDisplayName = user?.name || user?.displayName || t('topBar.user');
  const userRole = user?.rol || "Usuario";
  // Modo ERP visible para super-admin y usuarios con rol ERP (v1 roles
  // admin/kam/bp/comercial sin distinción de permisos — PLAN_ERP.md "Roles ERP")
  const canUseErp = erpEnabled && (user?.rol === 'super-admin' || Boolean(erpRole));
  // Usuario "solo ERP": rol de plataforma user con rol ERP — vive en el módulo
  // ERP y no ve el toggle de modos (Erwin, 2026-09-04)
  const erpOnly = canUseErp && user?.rol === 'user' && Boolean(erpRole);
  // ATS y Hunting: el botón solo con el módulo encendido en la organización
  const canUseAts = atsEnabled === true;
  const canUseHunting = huntingEnabled === true;
  const isSelectionView = !organization || !project;
  const userInitials = (userDisplayName || "U")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const menuItems = [];

  if (user && user.rol !== "user" && plan !== 'trial') {
    if (organization && project) {
      menuItems.push(
        <MenuItem
          key="change-project"
          sx={{
            color: "#FFFFFF",
            fontWeight: 500,
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.16)" },
          }}
          onClick={() => { handleChangeProject(); handleClose(); }}
        >
          {t('topBar.changeProject')}
        </MenuItem>
      );
      menuItems.push(
        <MenuItem
          key="change-organization"
          sx={{
            color: "#FFFFFF",
            fontWeight: 500,
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.16)" },
          }}
          onClick={() => { handleChangeOrganization(); handleClose(); }}
        >
          {t('topBar.changeOrganization')}
        </MenuItem>
      );
    } else if (organization && !project) {
      menuItems.push(
        <MenuItem
          key="change-organization-only"
          sx={{
            color: "#FFFFFF",
            fontWeight: 500,
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.16)" },
          }}
          onClick={() => { handleChangeOrganization(); handleClose(); }}
        >
          {t('topBar.changeOrganization')}
        </MenuItem>
      );
    }
  }

  menuItems.push(
    <MenuItem
      key="logout"
      sx={{
        color: "#FFFFFF",
        fontWeight: 500,
        "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.25)" },
      }}
      onClick={() => { handleLogout(); handleClose(); }}
    >
      {t('topBar.logout')}
    </MenuItem>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "#ffffff",
        width: isSelectionView ? "100%" : `calc(100% - ${drawerWidth}px)`,
        ml: isSelectionView ? 0 : `${drawerWidth}px`,
        transition: "width 0.3s ease, margin-left 0.3s ease",
        borderBottom: '1px solid #E5E7EB',
        height: 64,
      }}
    >
      <Toolbar>
        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

          {/* Toggle de modos (Evaluaciones/ATS/ERP/Hunting) — solo visible para organizaciones habilitadas cuando hay proyecto activo */}
          {project && !erpOnly && (canUseAts || canUseHunting || canUseErp) && (
            <ToggleButtonGroup
              value={appMode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode) {
                  setAppMode(newMode);
                  navigate(newMode === 'ats' ? '/ats' : newMode === 'erp' ? '/erp' : newMode === 'hunting' ? '/hunting' : '/home');
                }
              }}
              size="small"
              sx={{
                backgroundColor: '#F3F4F6',
                borderRadius: '8px',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '6px !important',
                  px: 2,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#6B7280',
                  textTransform: 'none',
                  '&.Mui-selected': {
                    backgroundColor: '#6A5AF9',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#5B4DE8' },
                  },
                  '&:hover': { backgroundColor: '#E5E7EB' },
                },
              }}
            >
              <ToggleButton value="legacy">{t('topBar.modeEvaluations')}</ToggleButton>
              {canUseAts && (
                <ToggleButton value="ats">ATS</ToggleButton>
              )}
              {/* Hunting (búsqueda proactiva en Apollo) — por organización, cualquier rol (Erwin, 2026-09-30) */}
              {canUseHunting && (
                <ToggleButton value="hunting">Hunting</ToggleButton>
              )}
              {canUseErp && (
                <ToggleButton value="erp">ERP</ToggleButton>
              )}
            </ToggleButtonGroup>
          )}

          <PointsUsage />

          <Tooltip title={t('topBar.title')}>
            <Box
              onClick={handleMenuClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                cursor: "pointer",
                pl: 2,
                borderLeft: "1px solid #D1D5DB",
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: "#EDE9FE",
                  color: "#6A5AF9",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                }}
              >
                {userInitials}
              </Avatar>

              <Box sx={{ lineHeight: 1.1 }}>
                <Typography
                  variant="body1"
                  sx={{ color: "#111827", fontWeight: 700 }}
                >
                  {userDisplayName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#374151",
                    fontWeight: 500,
                    textTransform: "capitalize",
                  }}
                >
                  {userRole}
                </Typography>
              </Box>

              <IconButton size="small" sx={{ color: logoColor }}>
                <ExpandMoreIcon />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              backgroundColor: (theme) => theme.palette.primary.main,
              border: "1px solid rgba(255, 255, 255, 0.25)",
              borderRadius: 2,
              boxShadow: "0px 10px 24px rgba(17, 24, 39, 0.16)",
              mt: 1,
              minWidth: 220,
            },
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {menuItems}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;